/**
 * Garmin Connect integration page.
 * Connect button: full browser redirect to GET /integrations/garmin/connect (OAuth PKCE).
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/layout/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, RefreshCw } from 'lucide-react';
import { getGarminStatus, initiateGarminConnect, disconnectGarmin, triggerGarminHistoryBackfill, type GarminStatusResponse } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

const GARMIN_DISCONNECT_EXPLANATION =
  'Disconnect removes the link between AthleteSpace and Garmin Connect. Your existing activities stay in AthleteSpace; we stop receiving new data from Garmin. You can reconnect anytime.';

export default function Garmin() {
  const { status } = useAuth();
  const queryClient = useQueryClient();
  const [detail, setDetail] = useState<GarminStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (status !== 'authenticated') return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getGarminStatus();
        if (!cancelled) setDetail(res);
      } catch {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [status]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await initiateGarminConnect();
      // Redirect happens in initiateGarminConnect; we only reach here on error
    } catch (e) {
      toast({
        title: 'Failed to connect Garmin',
        description: e instanceof Error ? e.message : 'Could not start Garmin OAuth',
        variant: 'destructive',
      });
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await triggerGarminHistoryBackfill();
      toast({
        title: 'Sync started',
        description: 'Garmin sync can take several minutes or longer. Activities will arrive as Garmin sends them. For instant sync, connect Strava.',
      });
    } catch (e) {
      toast({
        title: 'Sync failed',
        description: e instanceof Error ? e.message : 'Could not start Garmin sync',
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await disconnectGarmin();
      setDetail((d) => (d ? { ...d, connected: false, status: null, last_webhook_at: null, last_error: null, message: null, cursors: {} } : null));
      queryClient.invalidateQueries({ queryKey: ['integrations', 'status'] });
      toast({ title: 'Garmin disconnected', description: 'Your Garmin account has been disconnected' });
    } catch (e) {
      toast({
        title: 'Failed to disconnect',
        description: e instanceof Error ? e.message : 'Could not disconnect Garmin',
        variant: 'destructive',
      });
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="container max-w-lg py-8">
      <Link to="/settings" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to Settings
      </Link>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <img src="/garmin-connect-icon.png" alt="Garmin Connect" className="h-6 w-6 rounded" /> Garmin Connect
        </CardTitle>
        <CardDescription>
          Import workouts and physiological metrics from Garmin devices. Connect with OAuth 2.0 (PKCE).
          Garmin sync can take several minutes or longer. For instant sync, connect Strava.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading status…
          </div>
        )}
        {!loading && detail && (
          <>
            <p className="text-sm">
              Status: <strong>{detail.connected ? (detail.status ?? 'active') : 'Not connected'}</strong>
              {detail.last_webhook_at && (
                <span className="block text-muted-foreground">Last webhook: {new Date(detail.last_webhook_at).toLocaleString()}</span>
              )}
              {detail.message && <span className="block text-muted-foreground mt-1">{detail.message}</span>}
            </p>
            {detail.connected ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {GARMIN_DISCONNECT_EXPLANATION}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={handleSync} disabled={syncing}>
                    {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Sync / Load activities
                  </Button>
                  <Button variant="outline" onClick={handleDisconnect} disabled={disconnecting}>
                    {disconnecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Disconnect Garmin
                  </Button>
                </div>
              </div>
            ) : (
              <Button onClick={handleConnect} disabled={connecting}>
                {connecting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Connect Garmin (full browser redirect)
              </Button>
            )}
          </>
        )}
      </CardContent>
    </div>
  );
}
